import { ref } from 'vue';
import type { PassengerKey, PassengersData } from '@aviata/booking-data';
import type { flightEntity } from 'freedom-shared';

import {
  seatMapLib,
  type PassengersState,
  type SelectedSeatsState,
} from '@/widgets/seat-map';

type GetSeparatedQueriesByFlights<T> = {
  mappedFlights: flightEntity.types.MappedFlight[];
  separableEntity: Readonly<T[]>;
  flightIdx: number;
  flight: flightEntity.types.MappedFlight;
};

type GetSeatMapInitialStateByFlights = {
  mappedFlights: flightEntity.types.MappedFlight[];
  allFlightsSegmentsIdx: number[];
  passengers: PassengersData;
  initialState?: SelectedSeatsState;
};

export function separateByFlights<T>({
  mappedFlights,
  separableEntity,
  flight,
  flightIdx,
}: GetSeparatedQueriesByFlights<T>) {
  const currentFlightSegmentsStartIdx = mappedFlights
    .slice(0, flightIdx)
    .flatMap(flight => flight.segments).length;
  const currentFlightSegmentsEndIdx =
    currentFlightSegmentsStartIdx + flight.segments.length;
  return separableEntity.slice(
    currentFlightSegmentsStartIdx,
    currentFlightSegmentsEndIdx,
  );
}

export function getPassengersDataWithoutInfants(
  passengersData: PassengersData,
) {
  // should remove infants since infant can't have a seat.
  const passengersKeys = Object.keys(passengersData) as PassengerKey[];
  return passengersKeys
    .filter(passengerKey => !seatMapLib.isInfant(passengerKey))
    .reduce((acc, passengerKey) => {
      acc[passengerKey] = passengersData[passengerKey];
      return acc;
    }, {} as PassengersData);
}

export function getSeatMapInitialStateByFlights({
  mappedFlights,
  allFlightsSegmentsIdx,
  passengers,
  initialState,
}: GetSeatMapInitialStateByFlights) {
  const seatMapStateBySegment = ref<SelectedSeatsState>(
    initialState ||
      seatMapLib.getSeatMapInitialState({
        segmentIds: allFlightsSegmentsIdx,
        passengers,
      }),
  );
  return mappedFlights.map((flight, idx) =>
    separateByFlights<PassengersState>({
      mappedFlights,
      separableEntity: seatMapStateBySegment.value,
      flightIdx: idx,
      flight,
    }),
  );
}
